"use strict";
/*
This file is part of web3.js.

web3.js is free software: you can redistribute it and/or modify
it under the terms of the GNU Lesser General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

web3.js is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Lesser General Public License for more details.

You should have received a copy of the GNU Lesser General Public License
along with web3.js.  If not, see <http://www.gnu.org/licenses/>.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultAbiCoder = exports.AbiCoder = void 0;
// See: https://github.com/ethereum/wiki/wiki/Ethereum-Contract-ABI
const bytes_1 = require("@ethersproject/bytes");
const properties_1 = require("@ethersproject/properties");
const logger_1 = require("@ethersproject/logger");
const _version_js_1 = require("./_version.js");
const abstract_coder_js_1 = require("./coders/abstract-coder.js");
const address_js_1 = require("./coders/address.js");
const array_js_1 = require("./coders/array.js");
const boolean_js_1 = require("./coders/boolean.js");
const bytes_js_1 = require("./coders/bytes.js");
const fixed_bytes_js_1 = require("./coders/fixed-bytes.js");
const null_js_1 = require("./coders/null.js");
const number_js_1 = require("./coders/number.js");
const string_js_1 = require("./coders/string.js");
const tuple_js_1 = require("./coders/tuple.js");
const fragments_js_1 = require("./fragments.js");
const logger = new logger_1.Logger(_version_js_1.version);
const paramTypeBytes = new RegExp(/^bytes([0-9]*)$/);
const paramTypeNumber = new RegExp(/^(u?int)([0-9]*)$/);
class AbiCoder {
    coerceFunc;
    constructor(coerceFunc) {
        (0, properties_1.defineReadOnly)(this, 'coerceFunc', coerceFunc || null);
    }
    _getCoder(param) {
        switch (param.baseType) {
            case 'address':
                return new address_js_1.AddressCoder(param.name);
            case 'bool':
                return new boolean_js_1.BooleanCoder(param.name);
            case 'string':
                return new string_js_1.StringCoder(param.name);
            case 'bytes':
                return new bytes_js_1.BytesCoder(param.name);
            case 'array':
                return new array_js_1.ArrayCoder(this._getCoder(param.arrayChildren), param.arrayLength, param.name);
            case 'tuple':
                return new tuple_js_1.TupleCoder((param.components || []).map(component => this._getCoder(component)), param.name);
            case '':
                return new null_js_1.NullCoder(param.name);
        }
        // u?int[0-9]*
        let match = param.type.match(paramTypeNumber);
        if (match) {
            const size = parseInt(match[2] || '256');
            if (size === 0 || size > 256 || size % 8 !== 0) {
                logger.throwArgumentError(`invalid ${match[1]} bit length`, 'param', param);
            }
            return new number_js_1.NumberCoder(size / 8, match[1] === 'int', param.name);
        }
        // bytes[0-9]+
        match = param.type.match(paramTypeBytes);
        if (match) {
            const size = parseInt(match[1]);
            if (size === 0 || size > 32) {
                logger.throwArgumentError('invalid bytes length', 'param', param);
            }
            return new fixed_bytes_js_1.FixedBytesCoder(size, param.name);
        }
        return logger.throwArgumentError('invalid type', 'type', param.type);
    }
    _getWordSize() {
        return 32;
    }
    _getReader(data, allowLoose) {
        return new abstract_coder_js_1.Reader(data, this._getWordSize(), this.coerceFunc, allowLoose);
    }
    _getWriter() {
        return new abstract_coder_js_1.Writer(this._getWordSize());
    }
    getDefaultValue(types) {
        const coders = types.map(type => this._getCoder(fragments_js_1.ParamType.from(type)));
        const coder = new tuple_js_1.TupleCoder(coders, '_');
        return coder.defaultValue();
    }
    encode(types, values) {
        if (types.length !== values.length) {
            logger.throwError('types/values length mismatch', logger_1.Logger.errors.INVALID_ARGUMENT, {
                count: { types: types.length, values: values.length },
                value: { types, values },
            });
        }
        const coders = types.map(type => this._getCoder(fragments_js_1.ParamType.from(type)));
        const coder = new tuple_js_1.TupleCoder(coders, '_');
        const writer = this._getWriter();
        coder.encode(writer, values);
        return writer.data;
    }
    decode(types, data, loose) {
        const coders = types.map(type => this._getCoder(fragments_js_1.ParamType.from(type)));
        const coder = new tuple_js_1.TupleCoder(coders, '_');
        return coder.decode(this._getReader((0, bytes_1.arrayify)(data), loose));
    }
}
exports.AbiCoder = AbiCoder;
exports.defaultAbiCoder = new AbiCoder();
//# sourceMappingURL=abi-coder.js.map